import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { FaStar, FaChevronRight } from 'react-icons/fa'
import { HiSparkles } from 'react-icons/hi2'
import { s3Static } from 'utils/constants'

const stories = [
    {
        name: "Nisha Kumar",
        result: "12kg Lost in 6 Months",
        image: "personal-trainer/generated/nur-faridah-training-nisha.png",
        quote: "I struggled with weight gain for years... The trial session with Nur Faridah changed everything. She understood my challenges as an Indian woman...",
        link: "/sg/personal-trainer/success-stories#nisha",
        gradient: "from-purple-500 to-pink-500",
        badge: null
    },
    {
        name: "Kimberly Lim",
        result: "8kg Lost in 3 Months",
        image: "personal-trainer/generated/gerald-training-kimberly.png",
        quote: "As a busy executive, home training with Gerald was perfect. No commute time, just results. He transformed my living room into a gym...",
        link: "/sg/personal-trainer/success-stories#kimberly",
        gradient: "from-blue-500 to-cyan-500",
        badge: { text: "🏠 Home Training", color: "bg-blue-500/80" }
    },
    {
        name: "Agatha Morrison",
        result: "+6kg Muscle Gain",
        image: "personal-trainer/generated/serene-training-agatha.png",
        quote: "Serene showed me that women can be strong at any age. At 45, I'm lifting heavier than I imagined possible. She made the gym my sanctuary...",
        link: "/sg/personal-trainer/success-stories#agatha",
        gradient: "from-emerald-500 to-teal-500",
        badge: { text: "💪 Strength Training", color: "bg-emerald-500/80" }
    }
]

const StoryCard = ({ story }) => {
    return (
        <div className="bg-white/6 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden group hover:border-white/20 transition-all duration-300 shrink-0 w-[80vw] sm:w-[70vw] md:w-auto snap-center">
            {/* Image kept (social proof / E-E-A-T) but shorter on mobile to save a fold. */}
            <div className="relative h-40 md:h-64">
                <Image
                    src={s3Static + story.image}
                    fill
                    style={{ objectFit: 'cover', objectPosition: 'top' }}
                    alt={`${story.name}'s transformation`}
                    sizes="(max-width: 768px) 100vw, 33vw"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent" />
                
                {story.badge && (
                    <div className={`absolute top-4 left-4 ${story.badge.color} backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-bold`}>
                        {story.badge.text}
                    </div>
                )}
                
                <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-xl font-bold mb-1">{story.name}</h3>
                    <p className="text-sm text-white/80">{story.result}</p>
                </div>
            </div>
            
            <div className="p-5">
                <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                        <FaStar key={i} className="text-amber-400 text-sm" />
                    ))}
                </div>
                <p className="text-white/50 text-sm mb-4 line-clamp-3">
                    &quot;{story.quote}&quot;
                </p>
                <Link
                    href={story.link}
                    prefetch={false}
                    className={`text-sm md:text-base inline-block w-full text-center bg-linear-to-r ${story.gradient} text-white font-semibold py-3 px-6 rounded-full hover:shadow-lg hover:shadow-white/5 transition-all duration-300 hover:opacity-90`}
                >
                    Read Full Story →
                </Link>
            </div>
        </div>
    )
}

export const SuccessStories = () => {
    const [showArrow, setShowArrow] = useState(true);
    const scrollRef = useRef(null);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;
            setShowArrow(!isAtEnd);
        };

        container.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToNext = () => {
        const container = scrollRef.current;
        if (!container) return;
        container.scrollBy({ left: 300, behavior: 'smooth' });
    };

    return (
        <section className="py-8 md:py-16 bg-slate-900">
            <div className="max-w-7xl mx-auto px-4">
                {/* Section Header */}
                <div className="text-center mb-6 md:mb-12">
                    <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 px-4 py-2 rounded-full mb-4">
                        <HiSparkles className="text-green-400" />
                        <span className="text-xs md:text-sm font-semibold text-green-300">Success Stories</span>
                    </div>
                    
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2 md:mb-4">
                        Real Personal Training Transformations in Singapore
                    </h2>
                </div>

                {/* Stories Grid */}
                <div className="relative">
                    {showArrow && (
                        <div
                            className="absolute right-2 top-1/2 -translate-y-1/2 md:hidden z-10 cursor-pointer"
                            onClick={scrollToNext}
                        >
                            <div className="bg-white/10 backdrop-blur-sm rounded-full p-3 shadow-lg border border-white/10">
                                <FaChevronRight className="text-2xl text-green-400" />
                            </div>
                        </div>
                    )}

                    <div
                        ref={scrollRef}
                        className="flex md:grid md:grid-cols-3 gap-6 overflow-x-auto md:overflow-x-visible snap-x snap-mandatory md:snap-none scroll-smooth pb-4 scrollbar-hide"
                    >
                        {stories.map((story) => (
                            <StoryCard key={story.name} story={story} />
                        ))}
                    </div>
                </div>

                {/* View All Link */}
                <div className="text-center mt-10">
                    <Link
                        href="/sg/personal-trainer/success-stories"
                        prefetch={false}
                        className="text-sm md:text-base inline-flex items-center gap-2 px-8 py-4 bg-linear-to-r from-green-500 to-emerald-500 text-white font-bold rounded-full shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300 hover:opacity-90"
                    >
                        View All Success Stories
                        <FaChevronRight />
                    </Link>
                </div>
            </div>
        </section>
    )
}
